import type { CSSProperties } from "react"

interface StandingBadgeProps {
  position: number | null
}

function ordinalSuffix(n: number): string {
  const v = n % 100
  if (v >= 11 && v <= 13) return `${n}th`
  const lastDigit = n % 10
  if (lastDigit === 1) return `${n}st`
  if (lastDigit === 2) return `${n}nd`
  if (lastDigit === 3) return `${n}rd`
  return `${n}th`
}

function badgeColors(position: number | null) {
  if (position === null) {
    return {
      gradientTop: "oklch(0.28 0.04 55)",
      gradientBottom: "oklch(0.18 0.02 55)",
      textColor: "var(--muted-foreground)",
      lineColor: "oklch(0.45 0.04 55)",
      lineDimColor: "oklch(0.45 0.04 55 / 0.4)",
    }
  }
  if (position <= 3) {
    return {
      gradientTop: "oklch(0.30 0.10 145)",
      gradientBottom: "oklch(0.20 0.08 145)",
      textColor: "oklch(0.75 0.18 145)",
      lineColor: "oklch(0.75 0.18 145)",
      lineDimColor: "oklch(0.75 0.18 145 / 0.4)",
    }
  }
  return {
    gradientTop: "oklch(0.30 0.05 60)",
    gradientBottom: "oklch(0.20 0.04 60)",
    textColor: "oklch(0.65 0.05 60)",
    lineColor: "oklch(0.65 0.05 60)",
    lineDimColor: "oklch(0.65 0.05 60 / 0.4)",
  }
}

/** Compute parallel-offset V-polyline points for the pennant bottom. */
function offsetV(w: number, h: number, d: number, trim: number): string {
  const dx = w / 2
  const dy = h * 0.3
  const len = Math.sqrt(dx * dx + dy * dy)
  const nx = -dy / len
  const ny = dx / len

  const lx = 0 + nx * d
  const ly = h * 0.7 + ny * d
  const rx = w - nx * d
  const ry = h * 0.7 + ny * d

  const bisectorD = d / Math.cos(Math.atan2(dx, dy))
  const bmx = w / 2
  const bmy = h + bisectorD

  const tlx = lx + (bmx - lx) * trim
  const tly = ly + (bmy - ly) * trim
  const trx = rx + (bmx - rx) * trim
  const trY = ry + (bmy - ry) * trim

  return `${tlx},${tly} ${bmx},${bmy} ${trx},${trY}`
}

const W = 40
const H = 65 // golden ratio

export function StandingBadge({ position }: StandingBadgeProps) {
  const { gradientTop, gradientBottom, textColor, lineColor, lineDimColor } =
    badgeColors(position)

  const innerPoints = offsetV(W, H, 2, 0.08)
  const outerPoints = offsetV(W, H, 5, 0.18)

  return (
    <div
      className="relative flex flex-shrink-0 items-center justify-center overflow-visible"
      style={
        {
          width: W,
          height: H,
          "--emblem-bg": `linear-gradient(180deg, ${gradientTop}, ${gradientBottom})`,
        } as CSSProperties
      }
    >
      <div
        className="absolute inset-0 [clip-path:polygon(0_0,100%_0,100%_70%,50%_100%,0_70%)]"
        style={{ background: "var(--emblem-bg)" }}
      />
      <svg
        className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
        style={{ width: W, height: H }}
        viewBox={`0 0 ${W} ${H}`}
      >
        <polyline
          points={outerPoints}
          fill="none"
          stroke={lineDimColor}
          strokeWidth="1"
        />
      </svg>
      <svg
        className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
        style={{ width: W, height: H }}
        viewBox={`0 0 ${W} ${H}`}
      >
        <polyline
          points={innerPoints}
          fill="none"
          stroke={lineColor}
          strokeWidth="1"
        />
      </svg>
      {position === null ? (
        <span
          className="font-display relative mb-[30%] font-bold"
          style={{
            fontSize: "10px",
            letterSpacing: "0.1em",
            color: textColor,
          }}
        >
          NEW
        </span>
      ) : (
        <span
          className="font-display relative mb-[30%] text-lg leading-none font-bold"
          style={{ color: textColor }}
        >
          {ordinalSuffix(position)}
        </span>
      )}
    </div>
  )
}
